import ENV from '../constants/Env';
import { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useStyles } from '../components/rides/rideFormStyles';
import { useRef } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { useLoading } from '../components/layout/Loading';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Box, Button } from '@material-ui/core';
import { useAuth } from '../contexts/auth-context';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import {
  accountCustodianStorage,
  historyStorage,
  ridersStorage,
  upcomingRidesStorage,
} from '../storages/storage';
import { loader } from 'graphql.macro';
import { Custodian } from 'generated/graphql';
import { captureLog } from '../lib/error-handler';
const CUSTODIANS_QUERY = loader('../data/queries/CustodiansQuery.graphql');

type LookupState = 'loading' | 'notFound' | 'error';

export default function ImpersonateUser() {
  const history = useHistory();
  const classes = useStyles();
  const location = useLocation();
  const { storeBecomeCustodian } = useAuth();
  const apolloClient = useApolloClient();
  const lookedUpEmailRef = useRef<string | null>(null);
  const [lookupState, setLookupState] = useState<LookupState>('loading');
  const [, setLoading] = useLoading();

  const userEmail = new URLSearchParams(location.search).get('user');
  const isLookingUp = Boolean(userEmail) && lookupState === 'loading';

  const onBecome = useCallback(
    (becomeCustodian: Custodian) => {
      storeBecomeCustodian(apolloClient, { id: Number(becomeCustodian.id) });
      ridersStorage.clearStorage();
      upcomingRidesStorage.clearStorage();
      historyStorage.clearStorage();
      accountCustodianStorage.clearStorage();
    },
    [storeBecomeCustodian, apolloClient],
  );

  const [fetchCustodians] = useLazyQuery(CUSTODIANS_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    // useAuth returns a new storeBecomeCustodian on every render, so this effect
    // re-runs often. Only look up each email once.
    if (!userEmail || lookedUpEmailRef.current === userEmail) return;

    lookedUpEmailRef.current = userEmail;
    setLookupState('loading');

    (async () => {
      try {
        const { data, error } = await fetchCustodians({
          variables: { query: userEmail },
        });

        if (error) {
          captureLog(
            'error',
            `[Impersonate] - Error looking up custodian: ${error.message}`,
          );
          setLookupState('error');
          return;
        }

        const custodian = data?.custodians?.find(
          ({ email }: { email: string }) => email === userEmail,
        );

        if (!custodian) {
          captureLog(
            'warn',
            '[Impersonate] - No custodian matched the requested email',
          );
          setLookupState('notFound');
          return;
        }

        onBecome(custodian);
        const params = new URLSearchParams(location.search);
        params.delete('user');
        history.replace({ pathname: '/', search: params.toString() });
      } catch (error) {
        captureLog(
          'error',
          `[Impersonate] - Network error looking up custodian: ${error}`,
        );
        setLookupState('error');
      }
    })();
  }, [userEmail, location.search, fetchCustodians, history, onBecome]);

  // Show the progress bar in the header while looking up the user. Not using
  // useLoadingEffect because it has no cleanup, and the success path redirects
  // while the bar is still on, which would leave it stuck.
  useEffect(() => {
    setLoading(isLookingUp);
    return () => setLoading(false);
  }, [isLookingUp, setLoading]);

  // Portal has already switched to the chosen user, so its impersonate page
  // would only show Access Denied while we redirect.
  if (userEmail) {
    // Portal is still impersonating the user, so Magic would only show Access
    // Denied. Stopping impersonation switches Portal back to the admin.
    const stopImpersonatingButton = (
      <Button
        color="inherit"
        size="small"
        component={Link}
        to="/stop-impersonation"
      >
        Stop impersonating
      </Button>
    );

    return (
      <MainLayout>
        {lookupState === 'notFound' && (
          <Box>
            <Alert severity="warning" action={stopImpersonatingButton}>
              <AlertTitle>User Not Found</AlertTitle>
              We couldn't find a dashboard user with the email {userEmail}. Stop
              impersonating, then try again.
            </Alert>
          </Box>
        )}

        {lookupState === 'error' && (
          <Box>
            <Alert severity="error" action={stopImpersonatingButton}>
              <AlertTitle>Error</AlertTitle>
              Something went wrong while switching to {userEmail}. Stop
              impersonating, then try again.
            </Alert>
          </Box>
        )}
      </MainLayout>
    );
  }

  return (
    <MainLayout usePadding="no">
      <iframe
        data-test-id="portal-iframe"
        title="Portal Impersonate"
        src={`${ENV.portalUrl}/impersonate?embeddedInDashboard=true`}
        className={classes.iframe}
      />
    </MainLayout>
  );
}
